import { measureGfxPerformance } from './performance.js';

export type GfxViewportFramePhase = 'raster' | 'dom';

type FrameTask = () => void;

const FORCE_LAYOUT_AFTER_VIEWPORT_TASK_ATTRIBUTE =
  'streamlainPerformanceForceViewportTaskLayout';

function forceAndMeasureLayoutAfterTask(
  scope: object,
  phase: GfxViewportFramePhase,
  owner: object
) {
  if (
    typeof document === 'undefined' ||
    document.documentElement.dataset[
      FORCE_LAYOUT_AFTER_VIEWPORT_TASK_ATTRIBUTE
    ] !== 'true'
  ) {
    return;
  }

  const element =
    typeof HTMLElement !== 'undefined' && owner instanceof HTMLElement
      ? owner
      : null;
  measureGfxPerformance(
    scope,
    'viewport-style-layout-flush',
    () => {
      // Diagnostic-only read. It deliberately materializes pending style and
      // layout after exactly one viewport task so the invalidating owner can
      // be attributed instead of charging the whole coordinator callback.
      void document.documentElement.offsetWidth;
    },
    undefined,
    () => ({
      blockId: element?.dataset.blockId ?? null,
      flavour: element?.dataset.flavour ?? null,
      ownerType: owner.constructor?.name ?? 'unknown',
      phase,
      tagName: element?.tagName.toLowerCase() ?? null,
    })
  );
}

class GfxViewportFrameCoordinator {
  constructor(private readonly scope: object) {}

  private _frameId: number | null = null;

  private readonly _tasks: Record<
    GfxViewportFramePhase,
    Map<object, FrameTask>
  > = {
    raster: new Map(),
    dom: new Map(),
  };

  cancel(phase: GfxViewportFramePhase, owner: object) {
    this._tasks[phase].delete(owner);
  }

  schedule(phase: GfxViewportFramePhase, owner: object, task: FrameTask) {
    this._tasks[phase].set(owner, task);
    if (this._frameId !== null) return;

    this._frameId = requestAnimationFrame(() => {
      this._frameId = null;
      const rasterTasks = [...this._tasks.raster.entries()];
      const domTasks = [...this._tasks.dom.entries()];
      this._tasks.raster.clear();
      this._tasks.dom.clear();

      // Rasterize the world first, then commit every DOM-backed block against
      // that exact viewport state before the browser gets a paint boundary.
      rasterTasks.forEach(([owner, run]) => {
        run();
        forceAndMeasureLayoutAfterTask(this.scope, 'raster', owner);
      });
      domTasks.forEach(([owner, run]) => {
        run();
        forceAndMeasureLayoutAfterTask(this.scope, 'dom', owner);
      });
    });
  }
}

const viewportFrameCoordinators = new WeakMap<
  object,
  GfxViewportFrameCoordinator
>();

function getCoordinator(scope: object) {
  const existing = viewportFrameCoordinators.get(scope);
  if (existing) return existing;
  const coordinator = new GfxViewportFrameCoordinator(scope);
  viewportFrameCoordinators.set(scope, coordinator);
  return coordinator;
}

export function scheduleGfxViewportFrame(
  scope: object,
  phase: GfxViewportFramePhase,
  owner: object,
  task: FrameTask
) {
  getCoordinator(scope).schedule(phase, owner, task);
}

export function cancelGfxViewportFrame(
  scope: object,
  phase: GfxViewportFramePhase,
  owner: object
) {
  getCoordinator(scope).cancel(phase, owner);
}
